import { CalendarDays, Clock3, MapPin, Ticket as TicketIcon } from "lucide-react";
import "./EventAdmissionTicket.css";

export type EventAdmissionTicketProps = {
  ticket: {
    id: string;
    eventSlug: string;
    eventTitle: string;
    eventDate: string;
    eventTime: string;
    timezone: string;
    location: string;
    attendeeName: string;
    qrDataUrl: string;
    checkedInAt: string | null;
  };
};

function shortTicketReference(id: string) {
  const compact = id.replace(/[^a-z0-9]/gi, "").toUpperCase();
  return compact.slice(-8).padStart(8, "0");
}

function formatDate(value: string) {
  const parsed = new Date(`${value}T12:00:00`);
  if (!value || Number.isNaN(parsed.valueOf())) return value || "Date to be announced";
  return new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(parsed);
}

export default function EventAdmissionTicket({ ticket }: EventAdmissionTicketProps) {
  const checkedIn = Boolean(ticket.checkedInAt);
  const reference = shortTicketReference(ticket.id);

  return (
    <main className="event-admission" aria-label="Event admission ticket">
      <div className="event-admission__ambient" aria-hidden="true" />
      <article className={`admission-ticket${checkedIn ? " admission-ticket--used" : ""}`}>
        <div className="admission-ticket__art" aria-hidden="true">
          <div className="admission-ticket__brand">
            <span className="admission-ticket__brand-mark">CHI</span>
            <span>Alliance</span>
          </div>
          <div className="admission-ticket__art-copy">
            <span className="admission-ticket__eyebrow">A gathering worth remembering</span>
            <span className="admission-ticket__art-rule" />
            <span className="admission-ticket__art-caption">Your experience<br />begins here.</span>
          </div>
          <span className="admission-ticket__art-seal">CHI<br />✦</span>
        </div>

        <div className="admission-ticket__body">
          <header className="admission-ticket__heading">
            <div>
              <p className="admission-ticket__eyebrow admission-ticket__eyebrow--red">Official admission</p>
              <h1>{ticket.eventTitle}</h1>
            </div>
            <span className={`admission-ticket__status${checkedIn ? " admission-ticket__status--used" : ""}`} role="status">
              <span className="admission-ticket__status-dot" />
              {checkedIn ? "Checked in" : "Valid for entry"}
            </span>
          </header>

          <div className="admission-ticket__details">
            <div className="admission-ticket__detail">
              <CalendarDays aria-hidden="true" />
              <div><span>Date</span><strong>{formatDate(ticket.eventDate)}</strong></div>
            </div>
            <div className="admission-ticket__detail">
              <Clock3 aria-hidden="true" />
              <div><span>Time</span><strong>{ticket.eventTime}{ticket.timezone ? ` · ${ticket.timezone}` : ""}</strong></div>
            </div>
            <div className="admission-ticket__detail admission-ticket__detail--location">
              <MapPin aria-hidden="true" />
              <div><span>Venue</span><strong>{ticket.location}</strong></div>
            </div>
          </div>

          <div className="admission-ticket__attendee">
            <div>
              <span className="admission-ticket__eyebrow">Admit one</span>
              <strong>{ticket.attendeeName}</strong>
            </div>
            <div className="admission-ticket__reference">
              <span>Ticket reference</span>
              <strong>{reference}</strong>
            </div>
          </div>

          <div className="admission-ticket__perforation" aria-hidden="true"><span /><span /></div>

          <footer className="admission-ticket__entry">
            <div className="admission-ticket__qr-wrap">
              {ticket.qrDataUrl ? (
                <img className="admission-ticket__qr" src={ticket.qrDataUrl} alt={`Admission QR code, ticket ${reference}`} />
              ) : (
                <div className="admission-ticket__qr admission-ticket__qr--empty" aria-label="QR code unavailable">
                  <TicketIcon aria-hidden="true" />
                </div>
              )}
            </div>
            <div className="admission-ticket__entry-copy">
              <span className="admission-ticket__eyebrow admission-ticket__eyebrow--red">At the entrance</span>
              <h2>{checkedIn ? "This ticket has been used" : "Have your ticket ready"}</h2>
              <p>{checkedIn
                ? "Your admission was checked in. Keep this ticket for your records."
                : "Present this QR code to the event team for a quick check-in. Keep the code visible and avoid screenshots that crop its white border."}</p>
              {checkedIn && ticket.checkedInAt && (
                <p className="admission-ticket__checkin-time">
                  Checked in {new Intl.DateTimeFormat("en-US", { dateStyle: "medium", timeStyle: "short" }).format(new Date(ticket.checkedInAt))}
                </p>
              )}
            </div>
          </footer>
        </div>
      </article>
      <p className="event-admission__note">Please keep this ticket available when you arrive.</p>
    </main>
  );
}
